/**
 * Type of code being generated
 */
export type CodeAIType = 'css' | 'javascript';

/**
 * Mode for code generation
 */
export type CodeAIMode = 'replace' | 'append';

/**
 * Request interface for AI code generation
 */
export interface CodeAIRequest {
  /** Type of code to generate */
  type: CodeAIType;
  
  /** User's natural language prompt */
  prompt: string;
  
  /** ID of the preference page (backend will generate HTML from this) */
  preferencePageId?: string;
  
  /** Current HTML context from the page builder (deprecated - use preferencePageId) */
  html: string;
  
  /** Existing code in the section (for append/modify mode) */
  existingCode?: string;
  
  /** Additional CSS context (useful for JS generation) */
  cssContext?: string;
  
  /** Existing CSS from the page (for JS generation context) */
  existingCSS?: string;
  
  /** Generation mode */
  mode: CodeAIMode;
  
  /** Tenant ID for context */
  tenantId?: string;
}

/**
 * Response interface for AI code generation
 */
export interface CodeAIResponse {
  /** Generated code */
  code: string;
  
  /** Optional explanation of what was generated */
  explanation?: string;
  
  /** Validation results (especially for JavaScript) */
  validation?: ValidationResult;
  
  /** Success flag */
  success: boolean;
  
  /** Error message if any */
  error?: string;
}

/**
 * Validation result for generated code
 */
export interface ValidationResult {
  /** Whether the code is valid */
  isValid: boolean;
  
  /** Validation warnings */
  warnings?: string[];
  
  /** Security issues detected */
  securityIssues?: string[];
  
  /** Syntax errors */
  syntaxErrors?: string[];
}

/**
 * Preset template for quick code generation
 */
export interface CodeAIPreset {
  /** Preset identifier */
  id: string;
  
  /** Display name */
  label: string;
  
  /** Icon class (Font Awesome) */
  icon: string;
  
  /** Pre-filled prompt text */
  prompt: string;
  
  /** Type of code this preset generates */
  type: CodeAIType;
  
  /** Category for grouping */
  category?: string;
}

/**
 * CSS-specific presets
 */
export const CSS_PRESETS: CodeAIPreset[] = [
  {
    id: 'dark-mode',
    label: 'Dark Mode Theme',
    icon: 'fa-moon',
    prompt: 'Create a dark mode theme with dark backgrounds, light text, and good contrast for accessibility',
    type: 'css',
    category: 'Themes'
  },
  {
    id: 'modern-cards',
    label: 'Modern Card Design',
    icon: 'fa-layer-group',
    prompt: 'Add modern card styling with rounded corners, subtle shadows, and hover effects',
    type: 'css',
    category: 'Components'
  },
  {
    id: 'responsive',
    label: 'Mobile-First Responsive',
    icon: 'fa-mobile-alt',
    prompt: 'Make the page fully responsive with mobile-first approach, including flexible layouts and media queries',
    type: 'css',
    category: 'Layout'
  },
  {
    id: 'high-contrast',
    label: 'Accessible High Contrast',
    icon: 'fa-universal-access',
    prompt: 'Implement high contrast colors for improved accessibility following WCAG 2.1 AA standards',
    type: 'css',
    category: 'Accessibility'
  },
  {
    id: 'gradient-bg',
    label: 'Gradient Backgrounds',
    icon: 'fa-palette',
    prompt: 'Add beautiful gradient backgrounds with smooth color transitions',
    type: 'css',
    category: 'Themes'
  },
  {
    id: 'button-styles',
    label: 'Modern Button Styles',
    icon: 'fa-hand-pointer',
    prompt: 'Style all buttons with modern rounded design, hover effects, and smooth transitions',
    type: 'css',
    category: 'Components'
  }
];

/**
 * JavaScript-specific presets
 */
export const SCRIPT_PRESETS: CodeAIPreset[] = [
  {
    id: 'form-validation',
    label: 'Form Validation',
    icon: 'fa-check-circle',
    prompt: 'Add real-time form validation with error messages for email, required fields, and custom rules',
    type: 'javascript',
    category: 'Forms'
  },
  {
    id: 'char-counter',
    label: 'Character Counter',
    icon: 'fa-sort-numeric-up',
    prompt: 'Implement a character counter for textarea fields showing remaining characters',
    type: 'javascript',
    category: 'Forms'
  },
  {
    id: 'toggle-sections',
    label: 'Toggle Show/Hide Sections',
    icon: 'fa-eye-slash',
    prompt: 'Add functionality to toggle visibility of page sections with smooth animations',
    type: 'javascript',
    category: 'Interactions'
  },
  {
    id: 'smooth-scroll',
    label: 'Smooth Scroll Navigation',
    icon: 'fa-arrow-down',
    prompt: 'Implement smooth scrolling for anchor links and navigation',
    type: 'javascript',
    category: 'Navigation'
  },
  {
    id: 'loading-spinner',
    label: 'Loading Spinner on Submit',
    icon: 'fa-spinner',
    prompt: 'Add a loading spinner that appears when the form is submitted',
    type: 'javascript',
    category: 'Forms'
  },
  {
    id: 'confirm-dialog',
    label: 'Confirmation Dialog',
    icon: 'fa-exclamation-triangle',
    prompt: 'Add a confirmation dialog before submitting the form or performing critical actions',
    type: 'javascript',
    category: 'Interactions'
  }
];
